import React from 'react';
import CircleI from '@uber/base-icons/circle-i-outlined';
import TrashCan from '@uber/base-icons/trash-can';
import { useStyletron } from 'baseui';
import { Button, KIND as BUTTON_KIND, SIZE as BUTTON_SIZE } from 'baseui/button';
import { KIND as NOTIFICATION_KIND, Notification } from 'baseui/notification';
import { Select, SIZE as SELECT_SIZE } from 'baseui/select';
import { PLACEMENT as TOOLTIP_PLACEMENT, StatefulTooltip } from 'baseui/tooltip';
import { LabelSmall, ParagraphSmall } from 'baseui/typography';
import { FRAME_FIELD, RELATIONSHIP_CREATOR_CONSTANTS } from '../../constants';
import { NOTIFICATION_OVERRIDES, RELATIONSHIP_SELECT_OVERRIDES, RELATIONSHIP_TOOLTIP_OVERRIDES } from '../../overrides';
import type { DraftRelationshipT, FrameFieldT, SelectOptionT, TemporalScopeT } from '../../types/relationship';
import { FrameRangeInputs } from './frame-range-inputs';
type SelectLabelRendererT = ({
  option
}: {
  option: Readonly<{
    [key: string]: unknown;
  }>;
}) => React.ReactNode;
type TargetEntryProps = {
  index: number;
  entry: DraftRelationshipT;
  availableTargets: SelectOptionT[];
  temporalScope?: TemporalScopeT;
  sameInterval: boolean;
  sharedFrameRange: {
    startFrame?: number;
    endFrame?: number;
  };
  showRemove: boolean;
  frameRangeErrors?: {
    startError: string | undefined;
    endError: string | undefined;
  };
  error?: string;
  tooltipText?: string;
  onTargetChange: (id: string, targetId: string) => void;
  onFrameChange: (id: string, rangeIndex: number, field: FrameFieldT, value: number | undefined) => void;
  onRemove: (id: string) => void;
  onFocus?: (id: string) => void;
  noResultsMsg?: string;
  getOptionLabel?: SelectLabelRendererT;
  getValueLabel?: SelectLabelRendererT;
};
export const TargetEntry = ({
  index,
  entry,
  availableTargets,
  temporalScope,
  sameInterval,
  sharedFrameRange,
  showRemove,
  frameRangeErrors,
  error,
  tooltipText,
  onTargetChange,
  onFrameChange,
  onRemove,
  onFocus,
  noResultsMsg = 'No results',
  getOptionLabel,
  getValueLabel
}: TargetEntryProps) => {
  const [css, theme] = useStyletron();
  const selectedValue = entry.targetId ? availableTargets.filter(o => o.id === entry.targetId) : [];
  const frameDisabled = sameInterval;
  const firstRange = entry.frameRanges[0];
  const displayStartFrame = sameInterval ? sharedFrameRange.startFrame : firstRange?.startFrame;
  const displayEndFrame = sameInterval ? sharedFrameRange.endFrame : firstRange?.endFrame;
  return <div onClick={() => onFocus?.(entry.id)} className={css({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.sizing.scale300
  })}>
      <div className={css({
      display: 'flex',
      alignItems: 'center',
      gap: theme.sizing.scale200
    })}>
        <LabelSmall margin={0}>
          {`${RELATIONSHIP_CREATOR_CONSTANTS.TARGET_LABEL} ${index + 1}`}
        </LabelSmall>
        {tooltipText && <StatefulTooltip placement={TOOLTIP_PLACEMENT.top} content={() => tooltipText} overrides={RELATIONSHIP_TOOLTIP_OVERRIDES} showArrow>
            <CircleI size={14} />
          </StatefulTooltip>}
      </div>
      <div className={css({
      display: 'flex',
      alignItems: 'center',
      gap: theme.sizing.scale500
    })}>
        <div className={css({
        flex: 1
      })}>
          <Select size={SELECT_SIZE.compact} options={availableTargets} value={selectedValue} onChange={({
          value
        }) => {
          onTargetChange(entry.id, value[0] ? (value[0].id as string) : '');
        }} onFocus={() => onFocus?.(entry.id)} clearable={false} overrides={RELATIONSHIP_SELECT_OVERRIDES} getOptionLabel={getOptionLabel} getValueLabel={getValueLabel} placeholder={RELATIONSHIP_CREATOR_CONSTANTS.SELECT_TARGET_PLACEHOLDER} noResultsMsg={noResultsMsg} />
        </div>
        {showRemove && <Button kind={BUTTON_KIND.tertiary} size={BUTTON_SIZE.xSmall} onClick={() => onRemove(entry.id)} overrides={{
        BaseButton: {
          style: ({
            $theme
          }) => ({
            padding: $theme.sizing.scale300
          })
        }
      }}>
            <TrashCan size={14} />
          </Button>}
      </div>

      {!!temporalScope && <FrameRangeInputs temporalScope={temporalScope} startFrame={displayStartFrame} endFrame={displayEndFrame} disabled={frameDisabled} startError={frameRangeErrors?.startError} endError={frameRangeErrors?.endError} onStartFrameChange={v => onFrameChange(entry.id, 0, FRAME_FIELD.START, v)} onEndFrameChange={v => onFrameChange(entry.id, 0, FRAME_FIELD.END, v)} />}

      {error && <Notification kind={NOTIFICATION_KIND.negative} overrides={NOTIFICATION_OVERRIDES}>
          <ParagraphSmall margin={0}>{error}</ParagraphSmall>
        </Notification>}
    </div>;
};